/**
 * Device accessibility icons — mobile, headset, desktop.
 *
 * Rules:
 * - Mobile → also headset (Web/mobile content runs in MR/VR browsers too).
 * - Headset does NOT imply mobile (native VR apps stay headset-only).
 * - Set accessDevicesMobileOnly: true to opt out (e.g. venue-only native apps).
 *
 * Prefer explicit `accessDevices` on each experience record; fall back to inference.
 */

/** @typedef {'mobile'|'headset'|'desktop'} AccessDeviceSlug */

/** @typedef {{ device: AccessDeviceSlug, notRecommended?: boolean, label?: string }} AccessDeviceEntry */

const HEADSET_SURFACES = new Set(['headset-app', 'headset-browser']);
const MOBILE_SURFACES = new Set(['mobile-app', 'mobile-browser']);
const DESKTOP_SURFACES = new Set(['desktop-app', 'desktop-browser']);

const HEADSET_DEVICE_SLUGS = new Set(['quest', 'pcvr', 'psvr', 'steamvr', 'vision-pro']);
const MOBILE_DEVICE_SLUGS = new Set(['phone', 'phone-ar', 'ios', 'android', 'mobile']);

export const ACCESS_DEVICE_LABELS = {
  mobile: 'Phone or tablet',
  headset: 'VR / MR headset',
  desktop: 'Desktop or laptop'
};

/** Short labels for inline device line (Mobile · Headset · Desktop). */
export const ACCESS_DEVICE_SHORT_LABELS = {
  mobile: 'Mobile',
  headset: 'Headset',
  desktop: 'Desktop'
};

/** Normalize string or object entries into AccessDeviceEntry[]. */
export function normalizeAccessDeviceEntries(raw) {
  if (!Array.isArray(raw) || !raw.length) return [];
  return raw.map((entry) => {
    if (typeof entry === 'string') {
      const device = entry.replace(/-ticket$/, '');
      return { device, label: ACCESS_DEVICE_LABELS[device] || device };
    }
    if (entry && typeof entry === 'object' && entry.device) {
      return {
        device: entry.device,
        notRecommended: entry.notRecommended === true,
        label: entry.label || ACCESS_DEVICE_LABELS[entry.device] || entry.device
      };
    }
    return null;
  }).filter(Boolean);
}

function inferFromAccessSurface(exp) {
  const surfaces = exp.accessSurface || [];
  const devices = new Set();

  if (surfaces.some((s) => MOBILE_SURFACES.has(s))) devices.add('mobile');
  if (surfaces.some((s) => HEADSET_SURFACES.has(s))) devices.add('headset');
  if (surfaces.some((s) => DESKTOP_SURFACES.has(s))) devices.add('desktop');

  return devices;
}

function inferFromDeviceSlugs(exp) {
  const slugs = [
    ...(exp.devices || []),
    ...(exp.supportedDevices || []).filter((d) => d !== 'lbe' && d !== 'browser')
  ];
  const devices = new Set();

  if (slugs.some((d) => MOBILE_DEVICE_SLUGS.has(d))) devices.add('mobile');
  if (slugs.some((d) => HEADSET_DEVICE_SLUGS.has(d))) devices.add('headset');

  return devices;
}

function inferFromLegacy(exp) {
  const devices = new Set();
  const supported = exp.supportedDevices || [];

  if (exp.launchInBrowser || supported.includes('browser')) {
    devices.add('mobile');
    devices.add('desktop');
    if (supported.some((d) => HEADSET_DEVICE_SLUGS.has(d))) {
      devices.add('headset');
    }
  }

  if (supported.includes('phone-ar')) devices.add('mobile');
  if (supported.some((d) => HEADSET_DEVICE_SLUGS.has(d))) devices.add('headset');

  return devices;
}

/** @returns {AccessDeviceEntry[]} */
export function inferAccessDevices(exp) {
  if (!exp) return [];

  if (exp.isLBE || exp.isPhysicalVenue || exp.category === 'location-based') {
    return normalizeAccessDeviceEntries(['mobile', 'headset', 'desktop']);
  }

  const inferred = new Set([
    ...inferFromAccessSurface(exp),
    ...inferFromDeviceSlugs(exp),
    ...inferFromLegacy(exp)
  ]);

  // Immersive WebXR (AR character, spatial) — phone or headset, not flat desktop
  if (exp.deliveryTechnology?.includes('webxr') && exp.id !== 'escape-artist') {
    inferred.delete('desktop');
    inferred.add('mobile');
    inferred.add('headset');
  }

  // WebVR — headset only; no phone or flat desktop
  if (exp.id === 'escape-artist') {
    inferred.delete('mobile');
    inferred.delete('desktop');
    inferred.add('headset');
  }

  // Camera-based WebAR — phone + MR headset passthrough, not desktop
  if (exp.deliveryTechnology?.includes('webAR')) {
    inferred.delete('desktop');
    inferred.add('mobile');
    inferred.add('headset');
  }

  // Xi'an Lights WebAR portal — no desktop
  if (exp.id === 'xian-lights') {
    inferred.delete('desktop');
    inferred.add('mobile');
  }

  // Headset-native apps without browser access — drop desktop/mobile unless explicitly set
  if (
    exp.accessSurface?.length &&
    !exp.accessSurface.some((s) => MOBILE_SURFACES.has(s) || DESKTOP_SURFACES.has(s)) &&
    exp.accessSurface.some((s) => HEADSET_SURFACES.has(s))
  ) {
    inferred.delete('desktop');
    inferred.delete('mobile');
    inferred.add('headset');
  }

  const order = ['mobile', 'headset', 'desktop'];
  return applyDeviceRules(exp, order
    .filter((d) => inferred.has(d))
    .map((device) => ({ device, label: ACCESS_DEVICE_LABELS[device] })));
}

/** Mobile experiences also run on headset browsers; headset-only does not add mobile. */
function applyDeviceRules(exp, entries) {
  if (!entries.length) return entries;

  const byDevice = new Map(entries.map((e) => [e.device, e]));
  const slugs = new Set(byDevice.keys());

  if (slugs.has('mobile') && !exp.accessDevicesMobileOnly) {
    slugs.add('headset');
  }

  const order = ['mobile', 'headset', 'desktop'];
  return order
    .filter((d) => slugs.has(d))
    .map((device) => byDevice.get(device) || { device, label: ACCESS_DEVICE_LABELS[device] });
}

/** @returns {AccessDeviceEntry[]} */
export function getAccessDevices(exp) {
  if (!exp) return [];
  const explicit = normalizeAccessDeviceEntries(exp.accessDevices);
  if (explicit.length) return applyDeviceRules(exp, explicit);
  return inferAccessDevices(exp);
}

export function experienceSupportsDevice(exp, device) {
  if (!device || device === 'all') return true;
  return getAccessDevices(exp).some((entry) => entry.device === device);
}

export function getAccessDeviceAriaLabel(entries) {
  if (!entries.length) return '';
  return entries
    .map((e) => {
      const base = e.label || ACCESS_DEVICE_LABELS[e.device] || e.device;
      return e.notRecommended ? `${base} (headset recommended)` : base;
    })
    .join(', ');
}
